import {
  ChakraBox,
  ChakraFlex,
  ChakraGrid,
  CheckboxV2,
  FAIconName,
  Icon,
  InputGroupV2,
  TextInputV2,
  Tooltip,
  UniversalSelect,
} from '@equip.health/ui';
import SupportLinkedAccountsText from '~/components/common/SupportLinkedAccountsText';

import { DATE_PLACEHOLDER } from '~/lib/constants';
import { TextInputV2Type } from '~/lib/constants/inquiry.constants';
import { EquipAppSupportPermissionsList } from '~/lib/constants/supports.constants';
import { getSelectedUniversalSelectOption } from '~/lib/util/inquiry.util';
import {
  getFormattedOptions,
  isValidDateOfBirth,
  patientEnumMenuOptionsMapper,
} from '~/lib/util/patient.util';
import {
  PROVIDER_TOOLTIP_TEXT,
  SUPPORT_ACCESS_TOOLTIP_TEXT,
} from '~/lib/util/patientContact.util';
import {
  ERROR_MESSAGES,
  getMultiSelectedUniversalSelectOptions,
  isCharacterCountExceeded,
} from '~/lib/util/profile.util';
import {
  useGetEthnicitiesEnumQuery,
  useGetGendersEnumQuery,
  useGetProviderPronounsEnumQuery,
} from '~/store/services/enumerations';

interface ContactPersonalInfoProps {
  isSupportEmailEdited: boolean;
  personalInfo: ContactPersonalInfo;
  updateField: (field: string, value: boolean) => void;
  otherLinkedPatients: SupportOtherLinkedPatient[];
  hasPatientWithEmptyDateOfBirth: boolean;
  updateInvalidFields: (
    section: 'personalInfo',
    field: string,
    invalid: boolean,
  ) => void;
  updateSection: (
    section: 'personalInfo',
    field: keyof ContactPersonalInfo,
    value: string | string[] | boolean,
  ) => void;
}

const TOP_LEVEL_ID = 'contact-personal-info';

const ContactPersonalInfo = ({
  isSupportEmailEdited,
  personalInfo,
  updateInvalidFields,
  updateField,
  otherLinkedPatients,
  updateSection,
  hasPatientWithEmptyDateOfBirth,
}: ContactPersonalInfoProps) => {
  const { data: genders, isLoading: isGetGendersLoading } =
    useGetGendersEnumQuery();
  const gendersOptions = patientEnumMenuOptionsMapper(genders);
  const { data: pronouns, isLoading: isGetPronounsLoading } =
    useGetProviderPronounsEnumQuery();

  const pronounsOptions = patientEnumMenuOptionsMapper(pronouns);
  const { data: ethnicities, isLoading: isGetEthnicitiesLoading } =
    useGetEthnicitiesEnumQuery();
  const ethnicitiesOptions = patientEnumMenuOptionsMapper(ethnicities);
  const equipAppAccessMenuOptions = getFormattedOptions(
    EquipAppSupportPermissionsList,
  );

  const validateField = (
    field: keyof ContactPersonalInfo,
    value: string,
  ): boolean => {
    switch (field) {
      case 'firstName':
      case 'chosenName':
      case 'lastName':
      case 'middleName':
      case 'prefix':
      case 'suffix':
        return !isCharacterCountExceeded(value, 75);

      case 'dateOfBirth':
        return !value || isValidDateOfBirth(value);

      default:
        return true;
    }
  };

  const handleFieldChange = (
    field: keyof ContactPersonalInfo,
    value: string | string[] | boolean,
  ) => {
    const isFieldValid = validateField(field, String(value));
    updateInvalidFields('personalInfo', field, !isFieldValid);
    updateSection('personalInfo', field, value);
  };

  return (
    <ChakraFlex flexDirection="column" gap="18px">
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__prefix`}
        label="Prefix"
        value={
          personalInfo.prefix &&
          personalInfo.invalidFields.prefix &&
          ERROR_MESSAGES.NAME_LIMIT
        }
      >
        <TextInputV2
          id={`${TOP_LEVEL_ID}__prefix-input`}
          isInvalid={personalInfo.invalidFields.prefix}
          onChange={(value) => handleFieldChange('prefix', value)}
          value={personalInfo.prefix}
        />
      </InputGroupV2>

      <InputGroupV2
        id={`${TOP_LEVEL_ID}__first-name`}
        label="First name"
        shouldShowAsteriskWhenRequired
        value={
          personalInfo.firstName &&
          personalInfo.invalidFields.firstName &&
          ERROR_MESSAGES.NAME_LIMIT
        }
      >
        <TextInputV2
          id={`${TOP_LEVEL_ID}__first-name-input`}
          isInvalid={personalInfo.invalidFields.firstName}
          onChange={(value) => handleFieldChange('firstName', value)}
          value={personalInfo.firstName}
        />
      </InputGroupV2>

      <InputGroupV2
        id={`${TOP_LEVEL_ID}__chosen-name`}
        label="Chosen Name"
        value={
          personalInfo.chosenName &&
          personalInfo.invalidFields.chosenName &&
          ERROR_MESSAGES.NAME_LIMIT
        }
      >
        <TextInputV2
          id={`${TOP_LEVEL_ID}__chosen-name-input`}
          isInvalid={personalInfo.invalidFields.chosenName}
          onChange={(value) => handleFieldChange('chosenName', value)}
          value={personalInfo.chosenName}
        />
      </InputGroupV2>

      <InputGroupV2
        id={`${TOP_LEVEL_ID}__middle-name`}
        label="Middle Name"
        value={
          personalInfo.middleName &&
          personalInfo.invalidFields.middleName &&
          ERROR_MESSAGES.NAME_LIMIT
        }
      >
        <TextInputV2
          id={`${TOP_LEVEL_ID}__middle-name-input`}
          isInvalid={personalInfo.invalidFields.middleName}
          onChange={(value) => handleFieldChange('middleName', value)}
          value={personalInfo.middleName}
        />
      </InputGroupV2>

      <InputGroupV2
        id={`${TOP_LEVEL_ID}__last-name`}
        label="Last name"
        shouldShowAsteriskWhenRequired
        value={
          personalInfo.lastName &&
          personalInfo.invalidFields.lastName &&
          ERROR_MESSAGES.NAME_LIMIT
        }
      >
        <TextInputV2
          id={`${TOP_LEVEL_ID}__last-name-input`}
          isInvalid={personalInfo.invalidFields.lastName}
          onChange={(value) => handleFieldChange('lastName', value)}
          value={personalInfo.lastName}
        />
      </InputGroupV2>

      <InputGroupV2
        id={`${TOP_LEVEL_ID}__suffix`}
        label="Suffix"
        value={
          personalInfo.suffix &&
          personalInfo.invalidFields.suffix &&
          ERROR_MESSAGES.NAME_LIMIT
        }
      >
        <TextInputV2
          id={`${TOP_LEVEL_ID}__suffix-input`}
          isInvalid={personalInfo.invalidFields.suffix}
          onChange={(value) => handleFieldChange('suffix', value)}
          value={personalInfo.suffix}
        />
      </InputGroupV2>

      <InputGroupV2 id={`${TOP_LEVEL_ID}__gender`} label="Gender">
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__gender-select`}
          isLoading={isGetGendersLoading}
          isSingleSelect
          onChange={(option) => handleFieldChange('gender', option?.value)}
          options={gendersOptions}
          value={getSelectedUniversalSelectOption(
            personalInfo.gender,
            gendersOptions,
          )}
        />
      </InputGroupV2>

      <InputGroupV2 id={`${TOP_LEVEL_ID}__pronouns`} label="Pronouns">
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__pronouns-select`}
          isLoading={isGetPronounsLoading}
          isSingleSelect={false}
          onChange={(options) => {
            const selectedValues = options.map((option) => option?.value);
            handleFieldChange('pronouns', selectedValues);
          }}
          options={pronounsOptions}
          value={getMultiSelectedUniversalSelectOptions(
            personalInfo.pronouns,
            pronounsOptions,
          )}
          withCheckBox
        />
      </InputGroupV2>

      <InputGroupV2
        id={`${TOP_LEVEL_ID}__date-of-birth`}
        label="Date of birth"
        value={
          personalInfo.dateOfBirth &&
          personalInfo.invalidFields.dateOfBirth &&
          ERROR_MESSAGES.DOB
        }
      >
        <TextInputV2
          id={`${TOP_LEVEL_ID}__date-of-birth-input`}
          isInvalid={personalInfo.invalidFields.dateOfBirth}
          leftIcon={FAIconName.calendarDayRegular}
          onChange={(value) => handleFieldChange('dateOfBirth', value)}
          placeholder={DATE_PLACEHOLDER}
          type={TextInputV2Type.DATE}
          value={personalInfo.dateOfBirth}
        />
      </InputGroupV2>

      <InputGroupV2 id={`${TOP_LEVEL_ID}__ethnicity`} label="Race/Ethnicity">
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__ethnicity-select`}
          isLoading={isGetEthnicitiesLoading}
          isSingleSelect={false}
          onChange={(options) => {
            const selectedValues = options.map((option) => option?.value);
            handleFieldChange('ethnicities', selectedValues);
          }}
          options={ethnicitiesOptions}
          value={getMultiSelectedUniversalSelectOptions(
            personalInfo.ethnicities,
            ethnicitiesOptions,
          )}
          withCheckBox
        />
      </InputGroupV2>
      <ChakraGrid gap="36px" marginTop="18px">
        <InputGroupV2 id={`${TOP_LEVEL_ID}__tag`} label="Tags">
          <ChakraFlex flexDirection="column" gap="12px" marginTop="12px">
            <ChakraBox>
              <CheckboxV2
                id={`${TOP_LEVEL_ID}__tag-primary-contact`}
                isChecked={personalInfo.isPrimaryContact}
                label="Set as primary contact"
                onChange={(value: boolean) =>
                  handleFieldChange('isPrimaryContact', value)
                }
              />
            </ChakraBox>
            <ChakraBox>
              <CheckboxV2
                id={`${TOP_LEVEL_ID}__tag-emergency-contact`}
                isChecked={personalInfo.isEmergencyContact}
                label="Set as emergency contact"
                onChange={(value: boolean) =>
                  handleFieldChange('isEmergencyContact', value)
                }
              />
            </ChakraBox>
            <ChakraBox>
              <CheckboxV2
                id={`${TOP_LEVEL_ID}__tag-guarantor`}
                isChecked={personalInfo.isGuarantor}
                label="Set as guarantor"
                onChange={(value: boolean) =>
                  handleFieldChange('isGuarantor', value)
                }
              />
            </ChakraBox>
            <ChakraFlex alignItems="center" gap="8px">
              <ChakraBox>
                <CheckboxV2
                  id={`${TOP_LEVEL_ID}__tag-referring-provider`}
                  isChecked={personalInfo.isReferringProvider}
                  isDisabled={personalInfo.isGrantEquipAccess}
                  label="Referring provider"
                  onChange={(value: boolean) =>
                    handleFieldChange('isReferringProvider', value)
                  }
                />
              </ChakraBox>
              {personalInfo.isGrantEquipAccess && (
                <Tooltip
                  id={`${TOP_LEVEL_ID}__referring-provider-tooltip`}
                  placement="top-start"
                  tooltipText={PROVIDER_TOOLTIP_TEXT}
                >
                  <Icon
                    aria-label={FAIconName.infoCircleRegular}
                    name={FAIconName.infoCircleRegular}
                    size="sm"
                  />
                </Tooltip>
              )}
            </ChakraFlex>
            <ChakraFlex alignItems="center" gap="8px">
              <ChakraBox>
                <CheckboxV2
                  id={`${TOP_LEVEL_ID}__tag-ongoing-provider`}
                  isChecked={personalInfo.isOngoingProvider}
                  isDisabled={personalInfo.isGrantEquipAccess}
                  label="Ongoing provider"
                  onChange={(value: boolean) =>
                    handleFieldChange('isOngoingProvider', value)
                  }
                />
              </ChakraBox>
              {personalInfo.isGrantEquipAccess && (
                <Tooltip
                  id={`${TOP_LEVEL_ID}__ongoing-provider-tooltip`}
                  placement="top-start"
                  tooltipText={PROVIDER_TOOLTIP_TEXT}
                >
                  <Icon
                    aria-label={FAIconName.infoCircleRegular}
                    name={FAIconName.infoCircleRegular}
                    size="sm"
                  />
                </Tooltip>
              )}
            </ChakraFlex>
          </ChakraFlex>
        </InputGroupV2>
        <ChakraGrid gap="12px">
          <ChakraFlex alignItems="center" gap="8px">
            <ChakraBox>
              <CheckboxV2
                id={`${TOP_LEVEL_ID}__grant-access`}
                isChecked={personalInfo.isGrantEquipAccess}
                isDisabled={
                  personalInfo.isReferringProvider ||
                  personalInfo.isOngoingProvider ||
                  isSupportEmailEdited ||
                  hasPatientWithEmptyDateOfBirth
                }
                label="Grant access to Equip App"
                onChange={(value: boolean) =>
                  handleFieldChange('isGrantEquipAccess', value)
                }
              />
            </ChakraBox>
            {(personalInfo.isReferringProvider ||
              personalInfo.isOngoingProvider) && (
              <Tooltip
                id={`${TOP_LEVEL_ID}__grant-access-tooltip`}
                placement="top-start"
                tooltipText={SUPPORT_ACCESS_TOOLTIP_TEXT}
              >
                <Icon
                  aria-label={FAIconName.infoCircleRegular}
                  name={FAIconName.infoCircleRegular}
                  size="sm"
                />
              </Tooltip>
            )}
          </ChakraFlex>

          {personalInfo.isGrantEquipAccess && (
            <>
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__permissions`}
                label="Permissions"
                shouldShowAsteriskWhenRequired={personalInfo.isGrantEquipAccess}
              >
                <UniversalSelect
                  id={`${TOP_LEVEL_ID}__permissions-select`}
                  isDisabled={isSupportEmailEdited}
                  isInvalid={personalInfo.invalidFields.permissions}
                  isSingleSelect
                  onChange={(options) => {
                    handleFieldChange('permissions', options?.value);
                  }}
                  options={equipAppAccessMenuOptions}
                  value={getSelectedUniversalSelectOption(
                    personalInfo.permissions,
                    equipAppAccessMenuOptions,
                  )}
                />
              </InputGroupV2>
              <SupportLinkedAccountsText
                linkedAccountsCount={
                  otherLinkedPatients ? otherLinkedPatients.length : 0
                }
                onClick={() =>
                  updateField('isLinkedAccountsModalVisible', true)
                }
                topLevelId={`${TOP_LEVEL_ID}__equip-app-checkbox`}
              />
            </>
          )}
        </ChakraGrid>
      </ChakraGrid>
    </ChakraFlex>
  );
};

export default ContactPersonalInfo;
